import React, { useState, useEffect } from 'react';
import { Button, Badge } from './shared';

const DatabaseViewer = ({ service, subProjectId }) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  const [activeView, setActiveView] = useState('schema'); // schema, erd, data
  const [rowCount, setRowCount] = useState(null);
  const [schema, setSchema] = useState(null);

  const API_BASE = service?.url || '';

  // Fetch schema from API if subProjectId is provided
  useEffect(() => {
    if (subProjectId) {
      setSchemaLoading(true);
      setSchemaError(null);

      fetch(`/api/projects/sub-projects/${subProjectId}/database-schema`)
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to fetch schema');
          }
          return res.json();
        })
        .then(data => {
          // Transform API response to component format
          const transformedSchema = transformSchemaFromAPI(data);
          setSchema(transformedSchema);
          setSchemaLoading(false);
        })
        .catch(err => {
          console.error('Failed to load database schema:', err);
          setSchemaError(err.message);
          setSchemaLoading(false);
        });
    } else {
      // Use default hardcoded schema for project management database
      setSchema(getDefaultSchema());
    }
  }, [subProjectId]);

  // Transform API response to component format
  const transformSchemaFromAPI = (apiSchema) => {
    const dbType = apiSchema.database_type;

    if (dbType === 'mongodb') {
      // MongoDB uses collections instead of tables
      return {
        database_type: 'mongodb',
        database_name: apiSchema.database_name,
        tables: apiSchema.collections?.map(coll => ({
          name: coll.name,
          displayName: coll.name,
          icon: '📄',
          description: `${coll.document_count} documents`,
          color: 'bg-green-50 border-green-200',
          columns: coll.fields?.map(field => ({
            name: field.name,
            type: field.type,
            constraint: field.nullable ? '' : 'NOT NULL',
            description: ''
          })) || [],
          relationships: []
        })) || []
      };
    } else {
      // SQL databases (PostgreSQL, MySQL, SQLite)
      return {
        database_type: dbType,
        database_name: apiSchema.database_name,
        tables: apiSchema.tables?.map(table => ({
          name: table.name,
          displayName: table.name,
          icon: '📋',
          description: `${table.columns?.length || 0} columns`,
          color: 'bg-blue-50 border-blue-200',
          columns: table.columns?.map(col => ({
            name: col.name,
            type: col.type,
            constraint: [
              col.primary_key ? 'PRIMARY KEY' : '',
              !col.nullable ? 'NOT NULL' : '',
              col.default ? `DEFAULT ${col.default}` : ''
            ].filter(Boolean).join(', '),
            description: ''
          })) || [],
          relationships: [
            ...table.foreign_keys?.map(fk => ({
              type: 'foreign-key',
              target: fk.referred_table,
              columns: fk.constrained_columns,
              description: `FK to ${fk.referred_table}(${fk.referred_columns?.join(', ')})`
            })) || []
          ]
        })) || []
      };
    }
  };

  // Default schema for project management database
  const getDefaultSchema = () => ({
    tables: [
      {
        name: 'projects',
        displayName: 'Projects',
        icon: '📁',
        description: 'Main project entities',
        color: 'bg-blue-50 border-blue-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing project ID' },
          { name: 'name', type: 'VARCHAR(200)', constraint: 'NOT NULL', description: 'Project name' },
          { name: 'slug', type: 'VARCHAR(200)', constraint: 'UNIQUE, NOT NULL', description: 'URL-safe identifier' },
          { name: 'description', type: 'TEXT', constraint: '', description: 'Project description' },
          { name: 'category', type: 'VARCHAR(50)', constraint: 'NOT NULL', description: 'Project category' },
          { name: 'status', type: 'VARCHAR(50)', constraint: 'DEFAULT "planning"', description: 'Current status' },
          { name: 'priority', type: 'VARCHAR(50)', constraint: 'DEFAULT "medium"', description: 'Priority level' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
          { name: 'updated_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Last update timestamp' },
          { name: 'start_date', type: 'DATE', constraint: '', description: 'Project start date' },
          { name: 'target_date', type: 'DATE', constraint: '', description: 'Target completion date' },
          { name: 'completed_date', type: 'DATE', constraint: '', description: 'Actual completion date' },
          { name: 'version', type: 'VARCHAR(50)', constraint: 'DEFAULT "1.0.0"', description: 'Current version' },
          { name: 'tags', type: 'JSON', constraint: 'DEFAULT []', description: 'Project tags array' },
          { name: 'extra_metadata', type: 'JSON', constraint: 'DEFAULT {}', description: 'Additional metadata' },
          { name: 'client_name', type: 'VARCHAR(200)', constraint: '', description: 'Client/stakeholder name' },
          { name: 'client_contact', type: 'VARCHAR(200)', constraint: '', description: 'Client contact info' },
          { name: 'budget', type: 'FLOAT', constraint: '', description: 'Project budget' },
          { name: 'domain', type: 'VARCHAR(200)', constraint: '', description: 'Primary domain' },
          { name: 'sentry_project_ids', type: 'JSON', constraint: 'DEFAULT []', description: 'Sentry project IDs' },
        ],
        relationships: [
          { type: 'has-many', target: 'sub_projects', description: 'One project has many sub-projects' },
          { type: 'has-many', target: 'issues', description: 'One project has many issues' },
          { type: 'has-many', target: 'documentation', description: 'One project has many documents' },
          { type: 'has-many', target: 'integrations', description: 'One project has many integrations' },
        ]
      },
      {
        name: 'sub_projects',
        displayName: 'Sub-Projects',
        icon: '🗂️',
        description: 'Project components (frontend, backend, etc.)',
        color: 'bg-green-50 border-green-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → projects.id', description: 'Parent project' },
          { name: 'name', type: 'VARCHAR(200)', constraint: 'NOT NULL', description: 'Sub-project name' },
          { name: 'sub_project_type', type: 'VARCHAR(50)', constraint: 'NOT NULL', description: 'Type (frontend_web, backend_api, etc.)' },
          { name: 'description', type: 'TEXT', constraint: '', description: 'Description' },
          { name: 'repository_url', type: 'VARCHAR(500)', constraint: '', description: 'Git repository URL' },
          { name: 'repository_name', type: 'VARCHAR(200)', constraint: '', description: 'Repository name' },
          { name: 'branch', type: 'VARCHAR(100)', constraint: 'DEFAULT "main"', description: 'Default branch' },
          { name: 'technology_stack', type: 'JSON', constraint: 'DEFAULT []', description: 'Tech stack array' },
          { name: 'framework', type: 'VARCHAR(100)', constraint: '', description: 'Framework name' },
          { name: 'language', type: 'VARCHAR(50)', constraint: '', description: 'Programming language' },
          { name: 'deployment_url', type: 'VARCHAR(500)', constraint: '', description: 'Live URL' },
          { name: 'deployment_environment', type: 'VARCHAR(50)', constraint: '', description: 'Environment' },
          { name: 'build_command', type: 'VARCHAR(500)', constraint: '', description: 'Build command' },
          { name: 'start_command', type: 'VARCHAR(500)', constraint: '', description: 'Start command' },
          { name: 'status', type: 'VARCHAR(50)', constraint: 'DEFAULT "planning"', description: 'Status' },
          { name: 'progress_percentage', type: 'INTEGER', constraint: 'DEFAULT 0', description: 'Completion %' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
          { name: 'updated_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Update timestamp' },
        ],
        relationships: [
          { type: 'belongs-to', target: 'projects', description: 'Belongs to one project' },
          { type: 'has-many', target: 'issues', description: 'Can have many issues' },
          { type: 'has-many', target: 'documentation', description: 'Can have many documents' },
        ]
      },
      {
        name: 'issues',
        displayName: 'Issues',
        icon: '🐛',
        description: 'Issue tracking (bugs, features, tasks)',
        color: 'bg-red-50 border-red-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → projects.id', description: 'Parent project' },
          { name: 'sub_project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → sub_projects.id', description: 'Optional sub-project' },
          { name: 'title', type: 'VARCHAR(500)', constraint: 'NOT NULL', description: 'Issue title' },
          { name: 'description', type: 'TEXT', constraint: '', description: 'Issue description' },
          { name: 'issue_type', type: 'VARCHAR(50)', constraint: 'NOT NULL', description: 'Type (bug, feature, task)' },
          { name: 'status', type: 'VARCHAR(50)', constraint: 'DEFAULT "open"', description: 'Status' },
          { name: 'priority', type: 'VARCHAR(50)', constraint: 'DEFAULT "medium"', description: 'Priority' },
          { name: 'assignee', type: 'VARCHAR(200)', constraint: '', description: 'Assigned person' },
          { name: 'reporter', type: 'VARCHAR(200)', constraint: '', description: 'Reporter' },
          { name: 'resolution', type: 'VARCHAR(50)', constraint: '', description: 'Resolution type' },
          { name: 'tags', type: 'JSON', constraint: 'DEFAULT []', description: 'Tags array' },
          { name: 'labels', type: 'JSON', constraint: 'DEFAULT []', description: 'Labels array' },
          { name: 'estimated_hours', type: 'INTEGER', constraint: '', description: 'Estimated hours' },
          { name: 'actual_hours', type: 'INTEGER', constraint: '', description: 'Actual hours' },
          { name: 'story_points', type: 'INTEGER', constraint: '', description: 'Story points' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
          { name: 'due_date', type: 'DATETIME', constraint: '', description: 'Due date' },
        ],
        relationships: [
          { type: 'belongs-to', target: 'projects', description: 'Belongs to one project' },
          { type: 'belongs-to', target: 'sub_projects', description: 'Optionally belongs to sub-project' },
          { type: 'has-many', target: 'issue_comments', description: 'Can have many comments' },
        ]
      },
      {
        name: 'issue_comments',
        displayName: 'Issue Comments',
        icon: '💬',
        description: 'Comments on issues',
        color: 'bg-purple-50 border-purple-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'issue_id', type: 'INTEGER', constraint: 'FOREIGN KEY → issues.id', description: 'Parent issue' },
          { name: 'content', type: 'TEXT', constraint: 'NOT NULL', description: 'Comment content' },
          { name: 'author', type: 'VARCHAR(200)', constraint: '', description: 'Comment author' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
          { name: 'updated_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Update timestamp' },
        ],
        relationships: [
          { type: 'belongs-to', target: 'issues', description: 'Belongs to one issue' },
        ]
      },
      {
        name: 'documentation',
        displayName: 'Documentation',
        icon: '📄',
        description: 'Project documentation in Markdown',
        color: 'bg-yellow-50 border-yellow-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → projects.id', description: 'Parent project' },
          { name: 'sub_project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → sub_projects.id', description: 'Optional sub-project' },
          { name: 'title', type: 'VARCHAR(300)', constraint: 'NOT NULL', description: 'Document title' },
          { name: 'slug', type: 'VARCHAR(300)', constraint: 'NOT NULL', description: 'URL-safe identifier' },
          { name: 'doc_type', type: 'VARCHAR(50)', constraint: 'NOT NULL', description: 'Document type' },
          { name: 'status', type: 'VARCHAR(50)', constraint: 'DEFAULT "draft"', description: 'Document status' },
          { name: 'content', type: 'TEXT', constraint: '', description: 'Markdown content' },
          { name: 'summary', type: 'TEXT', constraint: '', description: 'Document summary' },
          { name: 'version', type: 'VARCHAR(50)', constraint: 'DEFAULT "1.0"', description: 'Document version' },
          { name: 'author', type: 'VARCHAR(200)', constraint: '', description: 'Author' },
          { name: 'tags', type: 'JSON', constraint: 'DEFAULT []', description: 'Tags array' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
        ],
        relationships: [
          { type: 'belongs-to', target: 'projects', description: 'Belongs to one project' },
          { type: 'belongs-to', target: 'sub_projects', description: 'Optionally belongs to sub-project' },
        ]
      },
      {
        name: 'integrations',
        displayName: 'Integrations',
        icon: '🔌',
        description: 'External service integrations',
        color: 'bg-indigo-50 border-indigo-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'project_id', type: 'INTEGER', constraint: 'FOREIGN KEY → projects.id', description: 'Parent project' },
          { name: 'name', type: 'VARCHAR(200)', constraint: 'NOT NULL', description: 'Integration name' },
          { name: 'integration_type', type: 'VARCHAR(50)', constraint: 'NOT NULL', description: 'Type (github, testing, etc.)' },
          { name: 'status', type: 'VARCHAR(50)', constraint: 'DEFAULT "pending"', description: 'Integration status' },
          { name: 'service_url', type: 'VARCHAR(500)', constraint: '', description: 'Service URL' },
          { name: 'external_id', type: 'VARCHAR(200)', constraint: '', description: 'External service ID' },
          { name: 'config', type: 'JSON', constraint: 'DEFAULT {}', description: 'Configuration' },
          { name: 'last_sync_at', type: 'DATETIME', constraint: '', description: 'Last sync timestamp' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
        ],
        relationships: [
          { type: 'belongs-to', target: 'projects', description: 'Belongs to one project' },
        ]
      },
      {
        name: 'prompts',
        displayName: 'AI Prompts',
        icon: '🤖',
        description: 'AI prompt library (independent)',
        color: 'bg-pink-50 border-pink-200',
        columns: [
          { name: 'id', type: 'INTEGER', constraint: 'PRIMARY KEY', description: 'Auto-incrementing ID' },
          { name: 'name', type: 'VARCHAR(200)', constraint: 'NOT NULL', description: 'Prompt name' },
          { name: 'content', type: 'TEXT', constraint: 'NOT NULL', description: 'Prompt content' },
          { name: 'tags', type: 'JSON', constraint: 'DEFAULT []', description: 'Tags array' },
          { name: 'created_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Creation timestamp' },
          { name: 'updated_at', type: 'DATETIME', constraint: 'DEFAULT NOW', description: 'Update timestamp' },
        ],
        relationships: []
      }
    ]
  });

  const fetchTableRowCount = async (tableName) => {
    try {
      const response = await fetch(`${API_BASE}/database/count/${tableName}`);
      if (response.ok) {
        const data = await response.json();
        return data.count;
      }
    } catch (err) {
      console.error(`Failed to fetch row count for ${tableName}:`, err);
    }
    return null;
  };

  const fetchTableData = async (tableName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/database/preview/${tableName}?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setTableData(data);
      }
    } catch (err) {
      console.error('Failed to fetch table data:', err);
      setTableData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTable && activeView === 'data') {
      fetchTableData(selectedTable.name);
      fetchTableRowCount(selectedTable.name).then(count => setRowCount(count));
    }
  }, [selectedTable, activeView]);

  const getRelationshipColor = (type) => {
    if (type === 'has-many') return 'text-blue-600';
    if (type === 'belongs-to') return 'text-green-600';
    return 'text-gray-600';
  };

  const getConstraintBadge = (constraint) => {
    if (!constraint) return null;
    if (constraint.includes('PRIMARY KEY')) return <Badge color="purple">PRIMARY KEY</Badge>;
    if (constraint.includes('FOREIGN KEY')) return <Badge color="blue">FOREIGN KEY</Badge>;
    if (constraint.includes('UNIQUE')) return <Badge color="yellow">UNIQUE</Badge>;
    if (constraint.includes('NOT NULL')) return <Badge color="red">NOT NULL</Badge>;
    if (constraint.includes('DEFAULT')) return <Badge color="gray">DEFAULT</Badge>;
    return <Badge color="gray">{constraint}</Badge>;
  };

  // Show loading state
  if (schemaLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading database schema...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (schemaError) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Schema</h3>
          <p className="text-gray-600 mb-4">{schemaError}</p>
          <p className="text-sm text-gray-500">
            Make sure the database is configured for this sub-project.
          </p>
        </div>
      </div>
    );
  }

  // Show message if no schema available
  if (!schema || !schema.tables || schema.tables.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <div className="text-gray-400 text-5xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Database Schema</h3>
          <p className="text-gray-600">
            {subProjectId
              ? 'This sub-project does not have a database configured yet.'
              : 'No database information available.'}
          </p>
        </div>
      </div>
    );
  }

  const databaseInfo = schema.database_name
    ? `${schema.database_type || 'SQLite'} • ${schema.database_name} • ${schema.tables.length} ${schema.database_type === 'mongodb' ? 'collections' : 'tables'}`
    : `SQLite • projects.db • ${schema.tables.length} tables`;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Database Schema Viewer</h2>
            <p className="text-sm text-gray-600 mt-1">
              {databaseInfo}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={activeView === 'schema' ? 'primary' : 'secondary'}
              onClick={() => setActiveView('schema')}
              size="sm"
            >
              📋 Schema
            </Button>
            <Button
              variant={activeView === 'erd' ? 'primary' : 'secondary'}
              onClick={() => setActiveView('erd')}
              size="sm"
            >
              🗺️ ERD
            </Button>
            {selectedTable && (
              <Button
                variant={activeView === 'data' ? 'primary' : 'secondary'}
                onClick={() => setActiveView('data')}
                size="sm"
              >
                🔍 Data
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Tables List */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Tables ({schema.tables.length})
            </h3>
            <div className="space-y-1">
              {schema.tables.map((table) => (
                <button
                  key={table.name}
                  onClick={() => setSelectedTable(table)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedTable?.name === table.name
                      ? 'bg-blue-50 border border-blue-200 text-blue-900'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{table.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{table.displayName}</div>
                      <div className="text-xs text-gray-500 truncate">{table.columns.length} columns</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {!selectedTable && activeView !== 'erd' ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <div className="text-6xl mb-4">🗄️</div>
                <p className="text-lg font-medium">Select a table to view its schema</p>
                <p className="text-sm mt-2">Choose from the list on the left</p>
              </div>
            </div>
          ) : activeView === 'erd' ? (
            // ERD View - Dynamic relationship diagram
            <div className="p-6 overflow-auto">
              <div className="bg-white rounded-lg border border-gray-200 p-6 min-w-max">
                <h3 className="text-lg font-semibold mb-6">Entity Relationship Diagram</h3>

                {/* Dynamic ERD - Show all tables with their relationships */}
                <div className="space-y-8">
                  {schema.tables.map((table, idx) => {
                    const hasFK = table.relationships && table.relationships.length > 0;
                    const fkRels = table.relationships?.filter(rel => rel.type === 'foreign-key') || [];

                    return (
                      <div key={table.name} className="flex items-start space-x-4">
                        {/* Table Card */}
                        <div
                          className={`${table.color || 'bg-blue-50 border-blue-200'} border-2 rounded-lg p-4 min-w-[250px] cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => {
                            setSelectedTable(table);
                            setActiveView('schema');
                          }}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-2xl">{table.icon || '📋'}</span>
                            <span className="font-bold text-gray-900">{table.displayName || table.name}</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div>PK: {table.primary_keys?.join(', ') || 'id'}</div>
                            <div>{table.columns?.length || 0} columns</div>
                            {fkRels.length > 0 && (
                              <div className="text-blue-600 font-medium">{fkRels.length} foreign key(s)</div>
                            )}
                          </div>
                        </div>

                        {/* Relationships */}
                        {fkRels.length > 0 && (
                          <div className="flex-1 space-y-2">
                            {fkRels.map((rel, relIdx) => (
                              <div key={relIdx} className="flex items-center space-x-2">
                                <div className="flex-1 border-t-2 border-dashed border-gray-400"></div>
                                <span className="text-xs text-gray-600">→</span>
                                <div className="bg-gray-100 border border-gray-300 rounded px-3 py-1 text-sm">
                                  <span className="font-semibold">{rel.target}</span>
                                  {rel.columns && (
                                    <span className="text-xs text-gray-600 ml-2">
                                      ({rel.columns.join(', ')})
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold mb-3 text-gray-700">Legend</h4>
                  <div className="flex flex-wrap gap-4 text-xs">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-0 border-t-2 border-dashed border-gray-400"></div>
                      <span>Foreign Key Relationship</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono">PK</span>
                      <span>Primary Key</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono">FK</span>
                      <span>Foreign Key</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>💡</span>
                      <span>Click on any table to view its detailed schema</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeView === 'schema' ? (
            // Schema View
            <div className="p-6">
              <div className={`bg-white rounded-lg border-2 ${selectedTable.color} shadow-sm`}>
                {/* Table Header */}
                <div className="border-b border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-3xl">{selectedTable.icon}</span>
                        <h3 className="text-2xl font-bold text-gray-900">{selectedTable.displayName}</h3>
                        <Badge color="gray">{selectedTable.name}</Badge>
                      </div>
                      <p className="text-gray-600">{selectedTable.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Columns:</span>
                      <span className="font-semibold">{selectedTable.columns.length}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Relationships:</span>
                      <span className="font-semibold">{selectedTable.relationships.length}</span>
                    </div>
                  </div>
                </div>

                {/* Columns */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                    Columns
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Column Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Constraints
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedTable.columns.map((column, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="font-mono text-sm font-medium text-gray-900">
                                {column.name}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Badge color="gray">{column.type}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {getConstraintBadge(column.constraint)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {column.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Relationships */}
                {selectedTable.relationships.length > 0 && (
                  <div className="border-t border-gray-200 p-6">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Relationships
                    </h4>
                    <div className="space-y-2">
                      {selectedTable.relationships.map((rel, idx) => (
                        <div
                          key={idx}
                          className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md"
                        >
                          <Badge color={rel.type === 'has-many' ? 'blue' : 'green'}>
                            {rel.type}
                          </Badge>
                          <span className="text-sm text-gray-600">→</span>
                          <button
                            onClick={() => {
                              const targetTable = schema.tables.find(t => t.name === rel.target);
                              if (targetTable) setSelectedTable(targetTable);
                            }}
                            className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {rel.target}
                          </button>
                          <span className="text-sm text-gray-600">— {rel.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Data View
            <div className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="border-b border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedTable.displayName} Data
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {rowCount !== null ? `${rowCount} total rows` : 'Showing preview'} • Limited to 10 rows
                      </p>
                    </div>
                    <Button
                      onClick={() => fetchTableData(selectedTable.name)}
                      disabled={loading}
                      size="sm"
                    >
                      {loading ? '⟳ Loading...' : '🔄 Refresh'}
                    </Button>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="text-4xl mb-2">⏳</div>
                        <p className="text-gray-600">Loading data...</p>
                      </div>
                    </div>
                  ) : tableData && tableData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {Object.keys(tableData[0]).map((key) => (
                              <th
                                key={key}
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tableData.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              {Object.entries(row).map(([key, value], cellIdx) => (
                                <td key={cellIdx} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                  {value === null ? (
                                    <span className="text-gray-400 italic">null</span>
                                  ) : typeof value === 'object' ? (
                                    <span className="text-xs font-mono text-purple-600">
                                      {JSON.stringify(value)}
                                    </span>
                                  ) : typeof value === 'boolean' ? (
                                    <Badge color={value ? 'green' : 'red'}>
                                      {value.toString()}
                                    </Badge>
                                  ) : (
                                    <span className="truncate max-w-xs block">
                                      {String(value)}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-4xl mb-2">📭</div>
                      <p>No data available or unable to fetch data</p>
                      <p className="text-sm mt-2">This feature requires backend API support</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseViewer;
